import { FC, useState, useEffect, useMemo, type CSSProperties } from 'react'
import {
  cargoDetailAttachmentDownloadUrl,
  cargoDetailAttachmentViewUrl,
  cargoFinalAttachmentDownloadUrl,
  cargoFinalAttachmentViewUrl,
  getCargoOperationById,
} from '../core/_requests'
import { CargoOperation } from '../core/_models'
import { toast } from 'react-toastify'
import { FileViewerModal } from '../../QHSE/components/FileViewerModal'
import { isLoaderDischargeApplicable } from '../../Management/core/vesselTypeOptions'
import { useAuth } from '../../auth'
import { OP } from '../core/operationPermissions'
import { useOperationsAccess } from '../core/useOperationsAccess'
import {
  canUserApproveOrReject,
  getCargoStatusBadgeClass,
  getCargoStatusLabel,
} from '../core/cargoApprovalWorkflow'
import ReviewCargoModal from './ReviewCargoModal'

interface ViewCargoModalProps { 
  isOpen: boolean
  onClose: () => void
  cargo: CargoOperation
  isCrew?: boolean
  onSuccess?: () => void
}

const WRAP_STYLE: CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const ViewCargoModal: FC<ViewCargoModalProps> = ({
  isOpen,
  onClose,
  cargo,
  isCrew = false,
  onSuccess,
}) => {
  const { currentUser } = useAuth()
  const { opCan } = useOperationsAccess()
  const [cargoData, setCargoData] = useState<CargoOperation | null>(null)
  const [loading, setLoading] = useState(true)
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  const [viewerUrl, setViewerUrl] = useState<string | null>(null)
  const [viewerDownloadUrl, setViewerDownloadUrl] = useState<string | null>(null)
  const [viewerTitle, setViewerTitle] = useState<string | undefined>(undefined)
  const [viewerFileName, setViewerFileName] = useState<string | undefined>(undefined)

  // Review modal state
  const [isReviewOpen, setIsReviewOpen] = useState(false)
  const [reviewAction, setReviewAction] = useState<'APPROVE' | 'REJECT'>('APPROVE')

  useEffect(() => {
    if (isOpen && cargo.id) {
      loadData()
    }
  }, [isOpen, cargo.id])

  const loadData = async () => {
    if (!cargo.id) return
    setLoading(true)
    try {
      const data = await getCargoOperationById(cargo.id)
      setCargoData(data)
    } catch (error: any) {
      toast.error('Error loading cargo operation', { position: 'top-center' })
    } finally {
      setLoading(false)
    }
  }

  const displayData = cargoData || cargo

  const formatDate = (iso?: string | null) => {
    if (!iso) return '-'
    try {
      return new Date(iso).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return '-'
    }
  }

  const cumulativeTotals = useMemo(() => {
    const totals: number[] = []
    let running = 0
    const details = displayData.details || []
    details.forEach((d, idx) => {
      const qty = d.quantityMt ?? 0
      running += qty
      totals[idx] = running
    })
    return totals
  }, [displayData])

  const showLoadersDischarge = useMemo(() => {
    const rawType =
      (displayData as any)?.vesselType ||
      (displayData as any)?.vessel?.vesselType ||
      (cargo as any)?.vesselType ||
      (cargo as any)?.vessel?.vesselType ||
      ''
    return isLoaderDischargeApplicable(rawType)
  }, [displayData, cargo])

  const isApprover = canUserApproveOrReject(
    currentUser,
    displayData,
    opCan(OP.APPROVE_CARGO_OPERATION)
  )
  const isPending = displayData.status === 'PENDING_APPROVAL'

  if (!isOpen) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '75vw',
          height: '75vh',
          maxWidth: '1400px',
          maxHeight: '900px',
        }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <div className='d-flex align-items-center gap-3 mx-6 mt-6'>
            <h5 className='modal-title text-dark mb-0'>View Cargo Report</h5>
            <span className={`badge fw-bold py-1 px-3 ${getCargoStatusBadgeClass(displayData.status)}`}>
              {displayData.status === 'APPROVED' && <i className='bi bi-lock-fill me-1'></i>}
              {displayData.status === 'PENDING_APPROVAL' && <i className='bi bi-hourglass-split me-1'></i>}
              {displayData.status === 'REJECTED' && <i className='bi bi-exclamation-circle-fill me-1'></i>}
              {getCargoStatusLabel(displayData.status)}
            </span>
          </div>
          <button type='button' className='btn-close mx-6 mt-6' onClick={onClose}></button>
        </div>

        <div className='flex-grow-1 overflow-auto px-4 py-3 mx-6'>
          {loading ? (
            <div className='text-center py-5'>
              <div className='spinner-border' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Approval status banner */}
              {displayData.status === 'APPROVED' && (
                <div className='alert alert-success d-flex align-items-center p-3 mb-4'>
                  <i className='bi bi-shield-check fs-2 text-success me-3'></i>
                  <div>
                    <div className='fw-bold text-success'>
                      Report Approved & Locked
                    </div>
                    <div className='fs-7 text-dark'>
                      Approved by <strong>{displayData.approvedByName || 'Crew Master / Admin'}</strong>
                      {displayData.approvedDateTime ? ` on ${formatDate(displayData.approvedDateTime)}` : ''}.
                      {displayData.approvalComments && (
                        <div className='mt-1 text-muted'>
                          <em>Comments: "{displayData.approvalComments}"</em>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {displayData.status === 'REJECTED' && (
                <div className='alert alert-danger d-flex align-items-center p-3 mb-4'>
                  <i className='bi bi-exclamation-triangle-fill fs-2 text-danger me-3'></i>
                  <div>
                    <div className='fw-bold text-danger'>
                      Report Returned for Revision
                    </div>
                    <div className='fs-7 text-dark'>
                      Rejected by <strong>{displayData.approvedByName || 'Crew Master / Admin'}</strong>
                      {displayData.approvedDateTime ? ` on ${formatDate(displayData.approvedDateTime)}` : ''}.
                    </div>
                    <div className='mt-1 bg-white p-2 rounded border border-danger fs-7'>
                      <strong>Rejection reason:</strong> {displayData.approvalComments || 'No reason provided'}
                    </div>
                  </div>
                </div>
              )}

              {displayData.status === 'PENDING_APPROVAL' && (
                <div className='alert alert-warning d-flex align-items-center p-3 mb-4'>
                  <i className='bi bi-hourglass-split fs-2 text-warning me-3'></i>
                  <div>
                    <div className='fw-bold text-warning'>
                      Awaiting Master / Company Admin Approval
                    </div>
                    <div className='fs-7 text-dark'>
                      Submitted by <strong>{displayData.submittedByName || 'Crew'}</strong>
                      {displayData.submittedDateTime ? ` on ${formatDate(displayData.submittedDateTime)}` : ''}.
                      {isApprover && ' You can approve or reject this report below.'}
                    </div>
                  </div>
                </div>
              )}

              <div className='row g-3 mb-4'>
                {!isCrew && (
                  <div className='col-md-4'>
                    <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                      Vessel
                    </label>
                    <div className='text-dark fs-6'>{displayData.vesselName || 'N/A'}</div>
                  </div>
                )}
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Voyage & Leg
                  </label>
                  <div className='text-dark fs-6 d-flex align-items-center gap-2'>
                    <span>{displayData.voyageNumber || '-'}</span>
                    {displayData.legSequenceNumber != null && (
                      <span
                        className='badge badge-light-primary fw-bold'
                        title={
                          displayData.legDeparturePort && displayData.legArrivalPort
                            ? `Leg ${displayData.legSequenceNumber}: ${displayData.legDeparturePort} -> ${displayData.legArrivalPort}`
                            : `Leg ${displayData.legSequenceNumber}`
                        }
                      >
                        Leg {displayData.legSequenceNumber}
                        {displayData.legDeparturePort && displayData.legArrivalPort
                          ? ` (${displayData.legDeparturePort} -> ${displayData.legArrivalPort})`
                          : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Breakup Type
                  </label>
                  <div className='text-dark fs-6'>{displayData.breakupType}</div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Created Date
                  </label>
                  <div className='text-dark fs-6'>{formatDate(displayData.createdDateTime)}</div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Updated Date
                  </label>
                  <div className='text-dark fs-6'>{formatDate(displayData.updatedDateTime)}</div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Total Cargo Quantity (MT)
                  </label>
                  <div className='text-dark fs-6'>
                    {displayData.totalCargoQtyMt !== undefined && displayData.totalCargoQtyMt !== null
                      ? displayData.totalCargoQtyMt
                      : '-'}
                  </div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Shipper as per B/L
                  </label>
                  <div className='text-dark fs-6'>{displayData.shipperAsPerBl || '-'}</div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Receiver as per B/L
                  </label>
                  <div className='text-dark fs-6'>{displayData.receiverAsPerBl || '-'}</div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Load Port(s)
                  </label>
                  <div className='text-dark fs-6'>{displayData.loadPorts || '-'}</div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Discharge Port(s)
                  </label>
                  <div className='text-dark fs-6'>{displayData.dischargePorts || '-'}</div>
                </div>
                <div className='col-md-4'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Heating Requirements
                  </label>
                  <div className='text-dark fs-6'>{displayData.heatingRequirements || '-'}</div>
                </div>
                <div className='col-md-8'>
                  <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#A1A5B7' }}>
                    Remarks
                  </label>
                  <div className='text-dark fs-6'>{displayData.remarks || '-'}</div>
                </div>
              </div>

              {/* Details table */}
              <div className='mb-4'>
                <h6 className='fw-bold mb-3'>
                  {displayData.breakupType === 'TANK' ? 'Tank Details' : 'Hold Details'}
                </h6>
                <div className='table-responsive border rounded'>
                  <table className='table table-bordered table-striped align-middle mb-0'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th style={{ minWidth: 140 }}>
                          {displayData.breakupType === 'TANK' ? 'Tank Name' : 'Hold Name'}
                        </th>
                        <th style={{ minWidth: 120 }}>Cargo Grade</th>
                        <th style={{ minWidth: 120 }}>Quantity (MT)</th>
                        <th style={{ minWidth: 140 }}>Cumulative Qty (MT)</th>
                        <th style={{ minWidth: 130 }}>Metric ton in Air</th>
                        {showLoadersDischarge && (
                          <th style={{ minWidth: 160 }}>No. of Loaders/Discharge</th>
                        )}
                        <th style={{ minWidth: 170 }}>Load/Discharge Rate</th>
                        <th style={{ minWidth: 170 }}>Ballast/Deballast Rate (m³/hr)</th>
                        <th style={{ minWidth: 150 }}>Dock Water Density (t/m³)</th>
                        <th style={{ minWidth: 170 }}>Max Draught Available HW (m)</th>
                        <th style={{ minWidth: 140 }}>Current Draught (m)</th>
                        <th style={{ minWidth: 100 }}>Attachment</th>
                      </tr>
                    </thead>
                    <tbody className='table-body text-start'>
                      {(displayData.details || []).map((detail, index) => (
                        <tr key={detail.id ?? index}>
                          <td className='text-center'>{index + 1}</td>
                          <td>{detail.cargoName || '-'}</td>
                          <td>{detail.cargoGrade || '-'}</td>
                          <td>{detail.quantityMt !== null && detail.quantityMt !== undefined ? detail.quantityMt : '-'}</td>
                          <td>{cumulativeTotals[index] ?? '-'}</td>
                          <td>{detail.metricTonInAir !== null && detail.metricTonInAir !== undefined ? detail.metricTonInAir : '-'}</td>
                          {showLoadersDischarge && (
                            <td>
                              {detail.noOfLoadersOrDischarge !== null && detail.noOfLoadersOrDischarge !== undefined
                                ? detail.noOfLoadersOrDischarge
                                : '-'}
                            </td>
                          )}
                          <td>
                            {detail.loadDischargeRateM3PerHr !== null && detail.loadDischargeRateM3PerHr !== undefined
                              ? `${detail.loadDischargeRateM3PerHr} ${detail.loadDischargeRateUnit || 'm³/hr'}`
                              : '-'}
                          </td>
                          <td>
                            {detail.ballastPumpingRateM3PerHr !== null && detail.ballastPumpingRateM3PerHr !== undefined
                              ? detail.ballastPumpingRateM3PerHr
                              : '-'}
                          </td>
                          <td>
                            {detail.dockWaterDensity !== null && detail.dockWaterDensity !== undefined
                              ? detail.dockWaterDensity
                              : '-'}
                          </td>
                          <td>
                            {detail.maxDraughtAvailableHw !== null && detail.maxDraughtAvailableHw !== undefined
                              ? detail.maxDraughtAvailableHw
                              : '-'}
                          </td>
                          <td>
                            {detail.currentDraughtMtrs !== null && detail.currentDraughtMtrs !== undefined
                              ? detail.currentDraughtMtrs
                              : '-'}
                          </td>
                          <td className='text-center'>
                            {detail.attachmentUrl ? (
                              <button
                                type='button'
                                className='btn btn-sm btn-link p-0'
                                onClick={() => {
                                  const cargoId = displayData.id || cargo.id
                                  const orderIdx = detail.orderIndex ?? index
                                  if (cargoId !== undefined) {
                                    setViewerUrl(cargoDetailAttachmentViewUrl(cargoId, orderIdx))
                                    setViewerDownloadUrl(cargoDetailAttachmentDownloadUrl(cargoId, orderIdx))
                                    setViewerTitle(`${detail.cargoName || 'Detail'} Attachment`)
                                    setViewerFileName(`detail_${orderIdx}`)
                                    setIsViewerOpen(true)
                                  }
                                }}
                              >
                                View
                              </button>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Final Attachments list */}
              <div className='mb-4'>
                <h6 className='fw-bold mb-3'>Final Documentation Attachments</h6>
                {(displayData.finalAttachments || []).length > 0 ? (
                  <div className='table-responsive border rounded'>
                    <table className='table table-bordered table-striped align-middle mb-0'>
                      <thead className='table-header text-start'>
                        <tr>
                          <th style={{ width: 50 }}>#</th>
                          <th style={{ minWidth: 200 }}>Document Name</th>
                          <th style={{ width: 120 }}>File</th>
                          <th style={{ minWidth: 200 }}>Remarks</th>
                        </tr>
                      </thead>
                      <tbody className='table-body text-start'>
                        {(displayData.finalAttachments || []).map((att, index) => (
                          <tr key={att.id ?? index}>
                            <td className='text-center'>{index + 1}</td>
                            <td>{att.documentName}</td>
                            <td className='text-center'>
                              {att.id && att.attachmentUrl ? (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-link p-0'
                                  onClick={() => {
                                    setViewerUrl(cargoFinalAttachmentViewUrl(att.id!))
                                    setViewerDownloadUrl(cargoFinalAttachmentDownloadUrl(att.id!))
                                    setViewerTitle(att.documentName)
                                    setViewerFileName(att.documentName)
                                    setIsViewerOpen(true)
                                  }}
                                >
                                  View
                                </button>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>{att.remarks || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='text-muted'>No final attachments available</div>
                )}
              </div>
            </>
          )}
        </div>

        <div className='border-top d-flex justify-content-between align-items-center px-4 py-3 flex-shrink-0 mx-6 mb-6'>
          <div>
            {displayData.status === 'APPROVED' && (
              <span className='badge badge-light-success fw-bold'>
                <i className='bi bi-lock-fill me-1'></i> Report is Locked
              </span>
            )}
          </div>
          <div className='d-flex gap-2'>
            {isApprover && isPending && (
              <>
                <button
                  type='button'
                  className='btn btn-danger btn-sm'
                  onClick={() => {
                    setReviewAction('REJECT')
                    setIsReviewOpen(true)
                  }}
                >
                  <i className='bi bi-x-circle me-1'></i> Reject
                </button>
                <button
                  type='button'
                  className='btn btn-success btn-sm'
                  onClick={() => {
                    setReviewAction('APPROVE')
                    setIsReviewOpen(true)
                  }}
                >
                  <i className='bi bi-check-circle me-1'></i> Approve
                </button>
              </>
            )}
            <button type='button' className='btn btn-light btn-sm' onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>

      <ReviewCargoModal
        isOpen={isReviewOpen}
        onClose={() => setIsReviewOpen(false)}
        cargo={displayData}
        initialAction={reviewAction}
        onSuccess={() => {
          loadData()
          onSuccess?.()
        }}
      />

      <FileViewerModal
        visible={isViewerOpen && !!viewerUrl}
        onClose={() => {
          setIsViewerOpen(false)
          setViewerUrl(null)
          setViewerDownloadUrl(null)
          setViewerTitle(undefined)
          setViewerFileName(undefined)
        }}
        title={viewerTitle}
        fileName={viewerFileName}
        viewUrl={viewerUrl || undefined}
        downloadUrl={viewerDownloadUrl || undefined}
      />
    </div>
  )
}

export default ViewCargoModal
